import { defineQuery } from "@rocicorp/zero";
import type { ExpressionBuilder } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql, type Schema } from "../schema";
import { channelMemberGuard } from "./access-filters";

export const reactionsQueries = {
  byMessage: defineQuery(z.string(), ({ args: msgId, ctx: { sub } }) =>
    zql.reactions
      .where("message_id", msgId)
      .whereExists("message", (q) =>
        q.whereExists("channel", channelMemberGuard(sub)),
      ),
  ),
  byChannel: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.reactions.whereExists("message", (q) =>
      q
        .where("channel_id", channelId)
        .whereExists("channel", channelMemberGuard(sub)),
    ),
  ),
  /** Main timeline only — excludes thread replies. For render selectors. */
  byChannelTimeline: defineQuery(
    z.string(),
    ({ args: channelId, ctx: { sub } }) =>
      zql.reactions.whereExists("message", (q) =>
        q
          .where("channel_id", channelId)
          .where("thread_id", "IS", null)
          .whereExists("channel", channelMemberGuard(sub)),
      ),
  ),
};

export const linkPreviewsQueries = {
  byMessage: defineQuery(z.string(), ({ args: msgId, ctx: { sub } }) =>
    zql.link_previews
      .where("message_id", msgId)
      .whereExists("message", (q) =>
        q.whereExists("channel", channelMemberGuard(sub)),
      ),
  ),
  byChannel: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.link_previews.whereExists("message", (q) =>
      q
        .where("channel_id", channelId)
        .whereExists("channel", channelMemberGuard(sub)),
    ),
  ),
  /** Main timeline only — excludes thread replies. For render selectors. */
  byChannelTimeline: defineQuery(
    z.string(),
    ({ args: channelId, ctx: { sub } }) =>
      zql.link_previews.whereExists("message", (q) =>
        q
          .where("channel_id", channelId)
          .where("thread_id", "IS", null)
          .whereExists("channel", channelMemberGuard(sub)),
      ),
  ),
};

export const attachmentsQueries = {
  byMessage: defineQuery(z.string(), ({ args: msgId, ctx: { sub } }) =>
    zql.attachments
      .where("message_id", msgId)
      .whereExists("message", (q) =>
        q.whereExists("channel", channelMemberGuard(sub)),
      ),
  ),
  byChannel: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.attachments.whereExists("message", (q) =>
      q
        .where("channel_id", channelId)
        .whereExists("channel", channelMemberGuard(sub)),
    ),
  ),
  /** Main timeline only — excludes thread replies. For render selectors. */
  byChannelTimeline: defineQuery(
    z.string(),
    ({ args: channelId, ctx: { sub } }) =>
      zql.attachments.whereExists("message", (q) =>
        q
          .where("channel_id", channelId)
          .where("thread_id", "IS", null)
          .whereExists("channel", channelMemberGuard(sub)),
      ),
  ),
};

export const channelPostingPermissionsQueries = {
  byChannel: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.channel_posting_permissions
      .where("channel_id", channelId)
      .whereExists("channel", channelMemberGuard(sub)),
  ),
  byChannelAndUser: defineQuery(
    z.object({ channelId: z.string(), userId: z.string() }),
    ({ args: { channelId, userId }, ctx: { sub } }) =>
      zql.channel_posting_permissions
        .where("channel_id", channelId)
        .where("user_id", userId)
        .whereExists("channel", channelMemberGuard(sub))
        .one(),
  ),
};

export const invitesQueries = {
  // Pending invites scoped to a workspace, for the Team view's "Invited"
  // section. Includes workspace invites (→ "Member") AND channel /
  // client_space invites (→ "Collaborator" / "Workspace link"), all of
  // which carry `workspace_id`. DM / group_dm are excluded — they're
  // personal, have no `workspace_id`, and live in the Home → Invites view.
  // `.related("channel" / "parent_channel")` supplies the context name the
  // row shows (#channel / space).
  byWorkspace: defineQuery(z.string(), ({ args: workspaceId, ctx: { sub } }) =>
    zql.invites
      .where("workspace_id", workspaceId)
      .where("type", "IN", ["workspace", "channel", "client_space"] as const)
      .where((eb: ExpressionBuilder<"invites", Schema>) =>
        eb.or(
          eb.cmp("creator_id", "=", sub),
          eb.exists("workspace", (wq) =>
            wq.whereExists("members", (mq) =>
              mq
                .where("user_id", sub)
                .where("removed_at", "IS", null)
                .where("role", "IN", [
                  "primary_owner",
                  "owner",
                  "admin",
                ] as const),
            ),
          ),
        ),
      )
      .related("channel")
      .related("parent_channel"),
  ),
  byCreator: defineQuery(({ ctx: { sub } }) =>
    zql.invites.where("creator_id", sub).orderBy("created_at", "desc"),
  ),
  /**
   * Invites addressed to the current user — cross-workspace, all types.
   * Powers the Home → Inbox + Invites surfaces. Matches on EITHER:
   *   • `other_user_id = sub` (invite created for a known user), OR
   *   • `invited_email = my email` (email-only invite — the common case)
   *     resolved via the `invited_user_by_email` relationship, which is
   *     a strict-equality join safe because of the lowercase invariant
   *     (migration 147 + client-side lowercasing).
   *
   * Excludes self-created invites (`creator_id` is the inviter, never the
   * recipient). Invites the user has explicitly DECLINED are excluded
   * CLIENT-SIDE: this query eager-loads the user's own decline record per
   * invite via `.related("declines", …)`, and `use-invites-data.ts` drops
   * rows whose `declines` array is non-empty. We can NOT express this as
   * `not(exists("declines"))` — Zero doesn't support negated exists on the
   * client (bugs.rocicorp.dev/issue/3438), and this query runs client-side.
   *
   * Returns related rows the UI needs to render: inviter (creator),
   * workspace, channel, and the client_space (parent_channel) for context.
   *
   * Does NOT filter by status (revoked / expired / used) — the client
   * derives status from the row fields so it can show "expired" /
   * "accepted" affordances. Callers wanting only actionable invites
   * filter client-side (revoked=false, use_count check, expires_at).
   */
  receivedByMe: defineQuery(({ ctx: { sub } }) =>
    zql.invites
      .where((eb: ExpressionBuilder<"invites", Schema>) =>
        eb.and(
          eb.cmp("creator_id", "!=", sub),
          eb.or(
            eb.cmp("other_user_id", "=", sub),
            eb.exists("invited_user_by_email", (uq) => uq.where("id", sub)),
          ),
        ),
      )
      .related("creator")
      .related("workspace")
      .related("channel")
      .related("parent_channel")
      // This user's decline record (0 or 1 row — (user_id, invite_id) is
      // unique) per invite. Client filters on `declines.length` to exclude
      // declined invites; an optimistic decline insert flips the array
      // non-empty and drops the row on the same tick.
      .related("declines", (dq) => dq.where("user_id", sub))
      .orderBy("created_at", "desc"),
  ),
  /**
   * Invites the current user has SENT — cross-workspace, all types.
   * Powers the Invites view's "Sent" section (DM outbox + others). Same
   * related-row shape as `receivedByMe` minus `creator` (the creator IS
   * the current user). Includes `other_user` so the UI can show the
   * named recipient for user-targeted invites.
   *
   * Status filtering is client-side, same rationale as `receivedByMe`.
   */
  sentByMe: defineQuery(({ ctx: { sub } }) =>
    zql.invites
      .where("creator_id", sub)
      .related("workspace")
      .related("channel")
      .related("parent_channel")
      .related("other_user")
      .related("invited_user_by_email")
      .orderBy("created_at", "desc"),
  ),
};

export const userChannelStatsQueries = {
  // Get all stats for the authenticated user (for sidebar badge counts)
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.user_channel_stats.where("user_id", sub),
  ),
  // Get stats for a specific channel for the authenticated user
  byChannelAndUser: defineQuery(
    z.string(),
    ({ args: channelId, ctx: { sub } }) =>
      zql.user_channel_stats
        .where("channel_id", channelId)
        .where("user_id", sub)
        .one(),
  ),
  // Get all stats for a channel (scoped to channels the user is a member of)
  byChannel: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.user_channel_stats
      .where("channel_id", channelId)
      .whereExists("channel", channelMemberGuard(sub)),
  ),
};
